import type {CurrentProject} from 'common/report/model-resources/projects';
import {ProjectDetails} from 'common/report/stores/project-details';
import {getProjectId, isCurrentProjectInternal, isUrlDefinedExperimentalFeatureEnabled} from 'common/report/util';
import {Location} from 'common/types/globals/location';

import {ProjectFeatureFlag} from '../constants';

export const featureSamplingUtils = {
  determineFeatureEligibility,
};

/**
 * Check deterministically whether a user or group is included in a feature rollout/test based on a
 * unique user/group string identifier and the desired percentage to include.
 */
function determineFeatureEligibility(
  userOrGroupId: string,
  feature: string,
  percentAllowed: number /* must be in range [0, 100] */,
): boolean {
  if (!(percentAllowed >= 0 && percentAllowed <= 100)) {
    throw new Error(`@percentAllowed argument must be in range [0, 100] inclusive`);
  }

  if (typeof userOrGroupId === `undefined` || userOrGroupId === null || userOrGroupId === ``) {
    throw new Error(`@userOrGroupId argument cannot be null or undefined or empty`);
  }
  if (typeof feature === `undefined` || feature === null || feature === ``) {
    throw new Error(`@feature argument cannot be null or undefined or empty`);
  }

  switch (percentAllowed) {
    case 0:
      return false;
    case 100:
      return true;
    default:
      break;
  }

  const key = userOrGroupId + feature;

  // Bernstein's hash: http://www.cse.yorku.ca/~oz/hash.html#djb2
  // The djb2 hash was chosen because of the claim that "it has excellent distribution and speed on many different sets of keys and table sizes",
  // but we have not validated whether it is statistically reliable and accurate to use in sampling users/groups for rollouts and analysis.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) + hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  const dart = (hash >>> 0) % 100;
  return dart < percentAllowed;
}

enum FeatureRolloutType {
  Project,
  Org,
}
class FeatureRollout {
  private type: FeatureRolloutType;
  private percentage: number;
  private featureflag: ProjectFeatureFlag;

  public name: FeatureRolloutName;

  constructor(params: {
    type: FeatureRolloutType;
    name: FeatureRolloutName;
    percentage: number;
    featureflag?: ProjectFeatureFlag;
  }) {
    Object.assign(this, params);
  }

  isEnabled(project: CurrentProject | ProjectDetails | null): boolean {
    if (this.type === FeatureRolloutType.Project) {
      const projectId = project?.id.toString() ?? getProjectId();
      return (
        featureSamplingUtils.determineFeatureEligibility(projectId, this.name.toString(), this.percentage) ||
        project?.hasFeatureFlag(this.featureflag)
      );
    } else {
      const orgId = project?.organizationId.toString();
      return orgId
        ? featureSamplingUtils.determineFeatureEligibility(orgId, this.name.toString(), this.percentage) ||
            project?.hasFeatureFlag(this.featureflag)
        : false;
    }
  }
}

// Add any gradual feature rollouts here
export enum FeatureRolloutName {
  CleanerUrls = `cleaner-urls`,
  DashboardUndoRedo = `dashboard-undo-redo`,
  InsightsPivotTable = `insights-pivot-table`,
  OnboardingChecklist = `onboarding-checklist`,
  InsightsNewCountMenu = `insights-new-count-menu`,
  CartAnalysis = `cart-analysis`,
  BetterBrokenState = `better-broken-state`,
  MultiAttribution = `multi-touch-attribution`,
  EventMetadata = `event-metadata`,
}

export const FeatureRollouts: {[key in FeatureRolloutName]: FeatureRollout} = {
  [FeatureRolloutName.CleanerUrls]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CleanerUrls,
    percentage: 100,
  }),
  [FeatureRolloutName.DashboardUndoRedo]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.DashboardUndoRedo,
    percentage: 100,
  }),
  [FeatureRolloutName.InsightsPivotTable]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.InsightsPivotTable,
    percentage: 0,
  }),
  [FeatureRolloutName.OnboardingChecklist]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.OnboardingChecklist,
    percentage: 0,
  }),
  [FeatureRolloutName.InsightsNewCountMenu]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.InsightsNewCountMenu,
    featureflag: ProjectFeatureFlag.InsightsNewCountMenu,
    percentage: 0,
  }),
  [FeatureRolloutName.CartAnalysis]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CartAnalysis,
    featureflag: ProjectFeatureFlag.CartAnalysis,
    percentage: 100,
  }),
  [FeatureRolloutName.BetterBrokenState]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.BetterBrokenState,
    featureflag: ProjectFeatureFlag.BetterBrokenState,
    percentage: 100,
  }),
  [FeatureRolloutName.MultiAttribution]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.MultiAttribution,
    featureflag: ProjectFeatureFlag.MultiAttribution,
    percentage: 100,
  }),
  [FeatureRolloutName.EventMetadata]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.EventMetadata,
    featureflag: ProjectFeatureFlag.EventMetadata,
    percentage: 50,
  }),
};

export function useRetentionConsistency() {
  return isUrlDefinedExperimentalFeatureEnabled(window.location, `retention_consistency`, () =>
    isCurrentProjectInternal(),
  );
}

export function useReportConsistency() {
  return isUrlDefinedExperimentalFeatureEnabled(window.location, `report_consistency`, false);
}

export function useSingleReport(location: Location = window.location) {
  return isUrlDefinedExperimentalFeatureEnabled(location, `single_report`, false);
}

export function isFoundationalTemplatesEnabled(currentProject) {
  if (!currentProject) {
    return false;
  }
  const isRolloutEnabled = featureSamplingUtils.determineFeatureEligibility(
    currentProject.organizationId.toString(),
    ProjectFeatureFlag.FoundationalTemplates,
    100, // @JakeW update rollout % for Foundational Templates here
  );
  return isRolloutEnabled || currentProject.hasFeatureFlag(ProjectFeatureFlag.FoundationalTemplates);
}
